const CRM_TICKETS_KEY = 'crmErpTickets';
const CRM_TICKET_SEQUENCE_KEY = 'crmErpTicketSequence';
const CRM_TICKETS_CHANGED_EVENT = 'crmTicketsChanged';

const fallbackCustomerNames = [
  { pattern: /bupa/i, name: 'BUPA Member' },
  { pattern: /veterinary|pet/i, name: 'Pet Owner' },
  { pattern: /healthcare|medical/i, name: 'Healthcare Member' },
  { pattern: /banking|credit card/i, name: 'Cardholder' },
  { pattern: /home insurance/i, name: 'Policyholder' },
];

const safelyParseTickets = (value) => {
  try {
    const parsed = JSON.parse(value || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getNextTicketNumber = () => {
  const nextSequence = Number(localStorage.getItem(CRM_TICKET_SEQUENCE_KEY) || '0') + 1;
  localStorage.setItem(CRM_TICKET_SEQUENCE_KEY, String(nextSequence));

  const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return `CASE-${datePart}-${String(nextSequence).padStart(4, '0')}`;
};

const normalizeLabel = (value = '') =>
  value.toLowerCase().replace(/[^a-z]/g, ' ').replace(/\s+/g, ' ').trim();

const extractCustomerName = (agent = {}) => {
  const rows = Array.isArray(agent.personaRows) ? agent.personaRows : [];
  const firstName = rows.find((row) => normalizeLabel(row.label) === 'first name')?.value;
  const lastName = rows.find((row) => normalizeLabel(row.label) === 'last name')?.value;

  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(' ');
  }

  const nameRow = rows.find((row) => {
    const label = normalizeLabel(row.label);
    return (
      (label === 'name' || label === 'customer name' || label === 'member name') &&
      !/card|plan|policy/.test(label)
    );
  });

  if (nameRow?.value) {
    return nameRow.value;
  }

  const fallback = fallbackCustomerNames.find((item) => item.pattern.test(agent.name || ''));
  return fallback?.name || 'Customer';
};

const truncate = (value = '', maxLength = 160) => {
  const text = String(value).replace(/\s+/g, ' ').trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
};

const buildInteractionSummary = (customerMessage, agentReplies = []) => {
  const firstReply = agentReplies.find(Boolean);
  const summary = [`Customer: ${truncate(customerMessage, 110)}`];

  if (firstReply) {
    summary.push(`Agent: ${truncate(firstReply, 130)}`);
  }

  return summary.join(' | ');
};

const getActionTaken = (agentName = '', customerMessage = '', agentReplies = []) => {
  const combinedText = [agentName, customerMessage, ...agentReplies].join(' ').toLowerCase();

  if (/battery|pixel|device|phone|tech|support/.test(combinedText)) {
    return 'Initial support intake completed';
  }

  if (/appointment|clinic|veterinary|pet|dog|cat/.test(combinedText)) {
    return 'Appointment request captured';
  }

  if (/bupa|coverage|hospital|doctor|travel|member/.test(combinedText)) {
    return 'Member details captured';
  }

  if (/pre.?auth|authorization|procedure|documents/.test(combinedText)) {
    return 'Authorization request logged';
  }

  if (/claim|insurance/.test(combinedText)) {
    return 'Claim details logged';
  }

  return 'Interaction logged';
};

const getNextActions = (agentName = '', customerMessage = '', agentReplies = []) => {
  const combinedText = [agentName, customerMessage, ...agentReplies].join(' ').toLowerCase();

  if (/appointment|clinic|veterinary|pet|dog|cat/.test(combinedText)) {
    return 'Confirm appointment details and send follow-up notes';
  }

  if (/bupa|coverage|hospital|doctor|travel|member/.test(combinedText)) {
    return 'Validate coverage and share provider options';
  }

  if (/pre.?auth|authorization|procedure|documents/.test(combinedText)) {
    return 'Collect documents and progress authorization';
  }

  if (/claim|insurance/.test(combinedText)) {
    return 'Review claim details and update customer';
  }

  return 'Review interaction and follow up';
};

export const getCrmTickets = () => safelyParseTickets(localStorage.getItem(CRM_TICKETS_KEY));

export const createCrmTicket = ({ agent, customerMessage, agentReplies }) => {
  const nextAction = getNextActions(agent?.name, customerMessage, agentReplies);
  const nextTicket = {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    ticketNumber: getNextTicketNumber(),
    customerName: extractCustomerName(agent),
    agentName: agent?.name || 'Agent',
    interactionSummary: buildInteractionSummary(customerMessage, agentReplies),
    actionTaken: getActionTaken(agent?.name, customerMessage, agentReplies),
    nextAction,
    nextActions: nextAction,
    createdAt: new Date().toISOString(),
  };
  const tickets = [nextTicket, ...getCrmTickets()];

  localStorage.setItem(CRM_TICKETS_KEY, JSON.stringify(tickets));
  window.dispatchEvent(
    new CustomEvent(CRM_TICKETS_CHANGED_EVENT, {
      detail: { tickets },
    })
  );

  return nextTicket;
};

export const CRM_TICKETS_CHANGED = CRM_TICKETS_CHANGED_EVENT;

